import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, TrendingUp, Award, BookOpen, Users, CheckCircle,
  AlertCircle, Pencil, Save, PlusCircle, GraduationCap, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MENTION_COLORS = {
  A: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  B: 'bg-blue-50 text-blue-700 border-blue-200',
  C: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  D: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  E: 'bg-orange-50 text-orange-700 border-orange-200',
  F: 'bg-red-50 text-red-700 border-red-200',
};

export const GradesPage = () => {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';

  return isStudent ? <StudentGradesView /> : <TeacherGradebookView />;
};

// ============ STUDENT VIEW ============
const StudentGradesView = () => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignmentGrades, setAssignmentGrades] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/gradebook/student/my-grades`).then(r => r.data).catch(() => null),
      axios.get(`${API_URL}/api/my-assignment-grades`).then(r => r.data).catch(() => [])
    ]).then(([grades, assignments]) => {
      setData(grades);
      setAssignmentGrades(Array.isArray(assignments) ? assignments : []);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="space-y-4 animate-slide-in" data-testid="grades-page"><h1 className="heading-2">Notes</h1><Card className="animate-pulse"><CardContent className="p-8"><div className="h-6 w-48 bg-gray-200 rounded" /></CardContent></Card></div>;

  const courses = data?.courses || [];
  const semAvg = data?.semester_averages || {};
  const annualAvg = data?.annual_average;
  const creditsEarned = data?.total_credits_earned || 0;
  const annualMention = data?.annual_mention;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="grades-page">
      <div>
        <h1 className="heading-2">Mes Notes</h1>
        <p className="text-gray-500 mt-1">Systeme LMD — Resultats academiques</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-2xl font-bold">{annualAvg ?? '—'}/20</p><p className="text-xs text-gray-500">Moyenne annuelle</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center"><Award className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-2xl font-bold">{creditsEarned}</p><p className="text-xs text-gray-500">Credits valides</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center"><BookOpen className="w-5 h-5 text-purple-600" /></div>
            <div><p className="text-2xl font-bold">{courses.length}</p><p className="text-xs text-gray-500">Cours</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-orange-600" /></div>
            <div>
              {annualMention ? (
                <Badge className={`text-sm font-bold ${MENTION_COLORS[annualMention.code]}`}>{annualMention.code}</Badge>
              ) : <p className="text-2xl font-bold">—</p>}
              <p className="text-xs text-gray-500">Mention</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium">{creditsEarned} / 180 credits (Licence)</span>
            <span className="text-gray-500">{((creditsEarned / 180) * 100).toFixed(1)}%</span>
          </div>
          <Progress value={(creditsEarned / 180) * 100} className="h-3" />
        </CardContent>
      </Card>

      {/* Semester Averages */}
      {Object.keys(semAvg).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.entries(semAvg).map(([sem, info]) => (
            <Card key={sem} className="border-l-4" style={{ borderLeftColor: info.average >= 10 ? '#10B981' : '#EF4444' }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Semestre {sem}</p>
                    <p className="text-2xl font-bold">{info.average}/20</p>
                    <p className="text-xs text-gray-400">{info.courses_count} cours — {info.total_credits} credits</p>
                  </div>
                  <Badge className={`text-lg font-bold ${MENTION_COLORS[info.mention?.code || 'F']}`}>
                    {info.mention?.code || '—'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Courses Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Resultats par cours</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Cours</TableHead>
                <TableHead className="text-center">Cr.</TableHead>
                <TableHead className="text-center">S.</TableHead>
                <TableHead className="text-center">Moy.</TableHead>
                <TableHead className="text-center">Mention</TableHead>
                <TableHead className="text-center">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {courses.map(c => (
                <TableRow key={c.course_id} data-testid={`grade-row-${c.code}`}>
                  <TableCell className="font-mono text-xs">{c.code}</TableCell>
                  <TableCell className="font-medium text-sm">{c.name}</TableCell>
                  <TableCell className="text-center">{c.credits}</TableCell>
                  <TableCell className="text-center text-gray-500">S{c.semester}</TableCell>
                  <TableCell className="text-center">
                    {c.average !== null ? <span className="font-bold">{c.average}/20</span> : <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {c.mention ? <Badge className={`text-xs ${MENTION_COLORS[c.mention.code]}`}>{c.mention.code} - {c.mention.label}</Badge> : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {c.average !== null ? (
                      c.passed ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <AlertCircle className="w-4 h-4 text-red-500 mx-auto" />
                    ) : <span className="text-gray-400 text-xs">En attente</span>}
                  </TableCell>
                </TableRow>
              ))}
              {courses.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">Aucune note disponible</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Assignment Grades */}
      {assignmentGrades.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Notes des Devoirs & TP</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {assignmentGrades.map(g => (
              <div key={g.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{g.assignment_title}</p>
                  <p className="text-xs text-gray-400">{g.course_name} ({g.course_code})</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">{g.score}/{g.max_points}</p>
                  {g.feedback && <p className="text-xs text-gray-500 max-w-[200px] truncate">{g.feedback}</p>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ TEACHER GRADEBOOK VIEW ============
const TeacherGradebookView = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [gradebook, setGradebook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddGrades, setShowAddGrades] = useState(false);
  const [classGroupFilter, setClassGroupFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API_URL}/api/online-courses/my-classrooms`).then(r => {
      const classrooms = Array.isArray(r.data) ? r.data : [];
      const allCourses = classrooms.flatMap(cr => (cr.courses || []).map(c => ({ ...c, promotion_label: cr.promotion_label })));
      setCourses(allCourses);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const fetchGradebook = useCallback(async (courseId) => {
    try {
      const params = {};
      if (classGroupFilter && classGroupFilter !== 'all') params.class_group_id = classGroupFilter;
      const res = await axios.get(`${API_URL}/api/gradebook/${courseId}`, { params });
      setGradebook(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement du cahier de cotes');
    }
  }, [classGroupFilter]);

  useEffect(() => {
    if (selectedCourse) fetchGradebook(selectedCourse);
  }, [selectedCourse, fetchGradebook]);

  if (loading) return <div className="space-y-4 animate-slide-in" data-testid="grades-page"><h1 className="heading-2">Cahier de Cotes</h1><Card className="animate-pulse"><CardContent className="p-8"><div className="h-6 w-48 bg-gray-200 rounded" /></CardContent></Card></div>;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="grades-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2">Cahier de Cotes</h1>
          <p className="text-gray-500 mt-1">Saisie et gestion des notes — Systeme LMD</p>
        </div>
        {selectedCourse && (
          <Button onClick={() => setShowAddGrades(true)} className="gap-2" data-testid="add-grades-button">
            <PlusCircle className="w-4 h-4" /> Saisir des notes
          </Button>
        )}
      </div>

      {/* Course Selector + Class Filter */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <Label className="text-sm mb-2 block">Selectionnez un cours</Label>
            <Select value={selectedCourse || ''} onValueChange={v => { setSelectedCourse(v); setClassGroupFilter('all'); }}>
              <SelectTrigger data-testid="gradebook-course-select"><SelectValue placeholder="Choisir un cours..." /></SelectTrigger>
              <SelectContent>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.code} — {c.name} ({c.credits} cr.)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {gradebook?.class_groups?.length > 0 && (
            <div>
              <Label className="text-sm mb-2 block">Filtrer par classe</Label>
              <Select value={classGroupFilter} onValueChange={setClassGroupFilter}>
                <SelectTrigger data-testid="gradebook-class-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les classes</SelectItem>
                  {gradebook.class_groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gradebook */}
      {gradebook && <GradebookTable gradebook={gradebook} onRefresh={() => fetchGradebook(selectedCourse)} />}

      {showAddGrades && gradebook && (
        <AddGradesDialog
          course={gradebook.course}
          students={gradebook.rows.map(r => r.student)}
          onClose={() => setShowAddGrades(false)}
          onSuccess={() => { setShowAddGrades(false); fetchGradebook(selectedCourse); }}
        />
      )}
    </div>
  );
};

// ============ GRADEBOOK TABLE ============
const GradebookTable = ({ gradebook, onRefresh }) => {
  const { course, columns, rows, stats } = gradebook;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold">{stats.total_students}</p><p className="text-xs text-gray-500">Etudiants</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold">{stats.graded_students}</p><p className="text-xs text-gray-500">Notes</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold">{stats.class_average}/20</p><p className="text-xs text-gray-500">Moy. classe</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600">{stats.pass_count}</p><p className="text-xs text-gray-500">Reussi</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-600">{stats.fail_count}</p><p className="text-xs text-gray-500">Echec</p></CardContent></Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {course.code} — {course.name} ({course.credits} credits)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white z-10 min-w-[60px]">Matr.</TableHead>
                <TableHead className="sticky left-[60px] bg-white z-10 min-w-[140px]">Nom</TableHead>
                {columns.map(col => (
                  <TableHead key={col.key} className="text-center min-w-[80px]">
                    <div className="text-xs">{col.label || col.type}</div>
                    <div className="text-[10px] text-gray-400">/{col.max_score}</div>
                  </TableHead>
                ))}
                <TableHead className="text-center bg-blue-50 min-w-[80px]">Moy./20</TableHead>
                <TableHead className="text-center bg-blue-50 min-w-[60px]">Mention</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.student.student_id} data-testid={`gradebook-row-${row.student.matricule}`}>
                  <TableCell className="sticky left-0 bg-white text-xs font-mono">{row.student.matricule}</TableCell>
                  <TableCell className="sticky left-[60px] bg-white text-sm font-medium">{row.student.name}</TableCell>
                  {columns.map(col => {
                    const grade = row.grades[col.key];
                    return (
                      <TableCell key={col.key} className="text-center">
                        {grade ? <span className={`font-medium ${grade.score < (grade.max_score * 0.5) ? 'text-red-600' : 'text-gray-900'}`}>{grade.score}</span> : <span className="text-gray-300">—</span>}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center bg-blue-50/50 font-bold">
                    {row.average !== null ? <span className={row.average < 10 ? 'text-red-600' : 'text-green-700'}>{row.average}</span> : '—'}
                  </TableCell>
                  <TableCell className="text-center bg-blue-50/50">
                    {row.mention ? <Badge className={`text-xs ${MENTION_COLORS[row.mention.code]}`}>{row.mention.code}</Badge> : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + 4} className="text-center py-8 text-gray-400">Aucun etudiant dans cette promotion</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ ADD GRADES DIALOG ============
const AddGradesDialog = ({ course, students, onClose, onSuccess }) => {
  const [gradeType, setGradeType] = useState('interrogation');
  const [label, setLabel] = useState('');
  const [maxScore, setMaxScore] = useState('20');
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const entries = Object.entries(scores)
      .filter(([, v]) => v !== '' && v !== undefined)
      .map(([studentId, score]) => ({ student_id: studentId, score: parseFloat(score) }));

    if (entries.length === 0) { toast.error('Saisissez au moins une note'); return; }
    if (!label.trim()) { toast.error('Donnez un libelle (ex: Interro 1)'); return; }

    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/gradebook/${course.id}/bulk-grades`, {
        grade_type: gradeType,
        label: label.trim(),
        session: 1,
        max_score: parseFloat(maxScore) || 20,
        grades: entries
      });
      toast.success(res.data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Saisir des notes — {course.code}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={gradeType} onValueChange={setGradeType}>
                <SelectTrigger data-testid="grade-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interrogation">Interrogation</SelectItem>
                  <SelectItem value="tp">TP</SelectItem>
                  <SelectItem value="devoir">Devoir</SelectItem>
                  <SelectItem value="examen">Examen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Libelle *</Label>
              <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex: Interro 1" data-testid="grade-label-input" />
            </div>
            <div>
              <Label>Max points</Label>
              <Input type="number" min="1" max="100" value={maxScore} onChange={e => setMaxScore(e.target.value)} data-testid="grade-max-input" />
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Matricule</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead className="w-[100px] text-center">Note /{maxScore}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map(st => (
                  <TableRow key={st.student_id}>
                    <TableCell className="text-xs font-mono">{st.matricule}</TableCell>
                    <TableCell className="text-sm">{st.name}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max={maxScore}
                        step="0.5"
                        className="h-8 text-center"
                        value={scores[st.student_id] ?? ''}
                        onChange={e => setScores(prev => ({ ...prev, [st.student_id]: e.target.value }))}
                        data-testid={`score-input-${st.matricule}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-between items-center pt-2">
            <p className="text-sm text-gray-500">{Object.values(scores).filter(v => v !== '' && v !== undefined).length} note(s) saisie(s)</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-grades-button">
                <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GradesPage;
